/**
 * getQuestionPrompt — single source of truth for extracting the prompt
 * string from a HITL question payload, shared between ora and the Outlook
 * plugin.
 *
 * Why a shared helper: HitlChoicePanel and HitlSubmitter previously each
 * shipped their own local copy of this helper with *different* key lists.
 * HitlChoicePanel read `pending_action`; HitlSubmitter did not. When the
 * substrate sent a `review_checkpoint` question with a `schema` field, the
 * plugin's SchemaHitlPanel routed to HitlSubmitter and the AM saw the
 * generic "Respond to continue" fallback instead of the actual prompt the
 * substrate had crafted (e.g. an MFA description with the number to enter).
 *
 * Canonical key precedence — earlier keys win:
 *   1. `question`          legacy plain-prompt shape
 *   2. `pending_action`    `review_checkpoint` question shape (substrate)
 *   3. `message`           generic fallback
 *   4. `prompt_text`       generic fallback
 *   5. `title`             generic fallback
 *
 * Returns the trimmed prompt string, or "Respond to continue" when no
 * recognised key holds a non-empty string. Callers that want a different
 * default can pass one via `fallback`.
 */
export function getQuestionPrompt(
  hitlQuestion: Record<string, unknown> | null | undefined,
  fallback: string = "Respond to continue",
): string {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return fallback;
  for (const key of QUESTION_PROMPT_KEYS) {
    const v = (hitlQuestion as Record<string, unknown>)[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return fallback;
}

/**
 * The exact key list `getQuestionPrompt` walks, in priority order.
 * Exported so tests and downstream callers can assert against it without
 * hard-coding the list a second time.
 */
export const QUESTION_PROMPT_KEYS: readonly string[] = [
  "question",
  "pending_action",
  "message",
  "prompt_text",
  "title",
] as const;
